/**
 * Blog editor half of the WebMCP capability.
 *
 * Supplies the editor read seam and the four editor write seams. Like every
 * other lane it publishes one `Partial<ReadDeps & WriteDeps>`; the mount picks
 * the members it is allowed to carry by name, so what this file omits is
 * unreachable from an editor command by construction.
 *
 * The live document is not a store this module can import — it is React state
 * inside `UniversalEditorPage`. `editorBridge.ts` is the one narrow channel to
 * it: the page publishes its handles on mount and every seam body reads that
 * bridge at CALL time, so a stale editor is impossible and a route with no
 * editor mounted simply refuses.
 *
 * Formatting and selection go straight to the live TipTap editor, exactly what
 * the toolbar buttons do. The persisted writes call the SAME service layer the
 * forms call (`blogCreationService`), never a page handler: page code can open a
 * confirm dialog a tool cannot answer, and the publish path in particular must
 * never await the interactive pre-publish gate.
 *
 * Authorization is spent structurally in `wrapWriteSeams` before any of these
 * run, and it re-checks tenant drift and expiry there, so these bodies carry no
 * authorization code of their own.
 */
import type {
  CommitAuthorization,
  EditorContext,
  ReadDeps,
  Receipt,
  Refusal,
  TenantId,
  WriteDeps,
} from './contract';
import { readEditorBridge } from './editorBridge';
import { blogCreationService } from '../services/blogCreationService';
import { buildGateItem, isPublishCancelledError } from '../lib/prePublishGate';

/** A publish that never resolves (an unanswered confirm dialog) must still
 *  terminate the tool call. This is the deadline for the whole publish leg. */
const PUBLISH_DEADLINE_MS = 25_000;
const PUBLISH_TIMED_OUT = Symbol('publish-timed-out');

function refuse(because: string, retryable = false): Refusal {
  return { kind: 'refused', because, retryable };
}

const NO_EDITOR = 'No blog editor is open in this tab, so there is no document to act on.';

export const editorSeams: Partial<ReadDeps & WriteDeps> = {
  /**
   * The live document's id, selection and mark state. Read straight off the
   * page's published bridge, so it reflects the buffer as it is now.
   */
  editorContext(_tenant: TenantId): EditorContext | null {
    const bridge = readEditorBridge();
    if (!bridge) return null;
    const editor = bridge.getEditor();
    if (!editor) return null;

    const { from, to, empty } = editor.state.selection;
    const selectedText = empty ? '' : editor.state.doc.textBetween(from, to, ' ');
    return {
      contentId: bridge.contentId,
      selectedText,
      selectionEmpty: empty,
      boldActive: editor.isActive('bold'),
      italicActive: editor.isActive('italic'),
    };
  },

  /**
   * Toggle bold or italic on the current selection, exactly as the toolbar
   * button does: `editor.chain().focus().toggle{Bold,Italic}().run()`. In-memory
   * only — never persisted, undone with one Ctrl+Z.
   */
  async editorFormat(
    _auth: CommitAuthorization,
    mark: 'bold' | 'italic',
  ): Promise<Receipt | Refusal> {
    const bridge = readEditorBridge();
    if (!bridge) return refuse(NO_EDITOR, true);
    const editor = bridge.getEditor();
    if (!editor) return refuse(NO_EDITOR, true);

    const chain = editor.chain().focus();
    const applied = mark === 'bold' ? chain.toggleBold().run() : chain.toggleItalic().run();
    if (!applied) {
      return refuse(
        `${mark} could not be applied to the current selection — the cursor may be in a place that does not take text formatting.`,
        true,
      );
    }
    const nowActive = editor.isActive(mark);
    return {
      summary: `Turned ${mark} ${nowActive ? 'on' : 'off'} for the current selection. This is a local edit; it is not saved until you save the article.`,
      facts: { mark, active: nowActive, persisted: false },
    };
  },

  /**
   * Persist the buffer to the draft row via the same `saveNow` the Save button
   * calls. Stays in our `blog_content` row; nothing reaches WordPress.
   */
  async editorSaveDraft(_auth: CommitAuthorization): Promise<Receipt | Refusal> {
    const bridge = readEditorBridge();
    if (!bridge) return refuse(NO_EDITOR, true);
    try {
      await bridge.saveNow();
    } catch {
      return refuse('The draft did not save. Nothing was published; try again.', true);
    }
    return {
      summary: 'Saved the article draft. This stays in Core and does not reach the client WordPress site.',
      facts: { contentId: bridge.contentId, reachedWordPress: false },
    };
  },

  /**
   * Set the canonical URL through the same `blogCreationService.patchContent`
   * the rail form calls. Edits our row's metadata; it reaches the public page
   * only if and when the article is later published.
   */
  async editorSetCanonical(
    auth: CommitAuthorization,
    canonicalUrl: string,
  ): Promise<Receipt | Refusal> {
    const bridge = readEditorBridge();
    if (!bridge) return refuse(NO_EDITOR, true);
    try {
      await blogCreationService.patchContent(
        bridge.contentId,
        { canonical_url: canonicalUrl },
        auth.tenant,
      );
    } catch {
      return refuse('The canonical URL did not save. Nothing was changed; try again.', true);
    }
    return {
      summary: `Set the canonical URL to ${canonicalUrl}. It takes effect on the public page when the article is published.`,
      facts: { contentId: bridge.contentId, canonicalUrl, reachedLiveSite: false },
    };
  },

  /**
   * Set the JSON-LD article description through the same
   * `blogCreationService.updateAuthoredSchema` the rail's structured-data card
   * calls. It lands in the authored-schema fields with the edited-guard stamp,
   * so a later metadata re-run will not clobber it.
   */
  async editorSetJsonldDescription(
    auth: CommitAuthorization,
    description: string,
  ): Promise<Receipt | Refusal> {
    const bridge = readEditorBridge();
    if (!bridge) return refuse(NO_EDITOR, true);
    let result: unknown;
    try {
      result = await blogCreationService.updateAuthoredSchema(
        bridge.contentId,
        { article_description: description },
        auth.tenant,
      );
    } catch {
      return refuse('The JSON-LD description did not save. Nothing was changed; try again.', true);
    }
    // Read back from the PATCH response rather than assume: the endpoint accepts
    // the field only when it can compose it into `postgen_schema.fields`, and it
    // returns the article's live `postgen_schema_fields`. If the value did not
    // land there, the write was a no-op and a success receipt would be a lie.
    const persisted = (
      result as { postgen_schema_fields?: { article_description?: string | null } | null }
    ).postgen_schema_fields?.article_description;
    if (typeof persisted !== 'string' || persisted.trim() !== description.trim()) {
      return refuse(
        "The JSON-LD description did not persist — the article's schema was not updated. This can happen " +
          'when the article has no generated schema yet; nothing was changed.',
        true,
      );
    }
    return {
      summary: 'Set the article JSON-LD description (confirmed saved). It takes effect on the public page when the article is published.',
      facts: { contentId: bridge.contentId, chars: description.length, reachedLiveSite: false },
    };
  },

  /**
   * Set the Open Graph / social-share image URL through the same
   * `blogCreationService.patchContent` (`og_image_url`) the rail's image card
   * calls.
   */
  async editorSetOgImage(
    auth: CommitAuthorization,
    imageUrl: string,
  ): Promise<Receipt | Refusal> {
    const bridge = readEditorBridge();
    if (!bridge) return refuse(NO_EDITOR, true);
    try {
      await blogCreationService.patchContent(
        bridge.contentId,
        { og_image_url: imageUrl },
        auth.tenant,
      );
    } catch {
      return refuse('The Open Graph image did not save. Nothing was changed; try again.', true);
    }
    return {
      summary: `Set the Open Graph image to ${imageUrl}. It is what social platforms show when the published article is shared.`,
      facts: { contentId: bridge.contentId, imageUrl, reachedLiveSite: false },
    };
  },

  /**
   * Publish the article LIVE to the client's WordPress site.
   *
   * Goes through `blogCreationService.publish`, the SAME path the Publish button
   * uses — so the agent's live-publish path is gated exactly like the human's:
   * the metadata warn-and-confirm gate, the slug-collision check, and the
   * recorded acknowledgement. The gate shows a dialog only when there are
   * violations and fails open when no handler is registered, so a clean article
   * publishes without a prompt; when the operator declines the confirmation the
   * service throws `PublishCancelledError`, which becomes a refusal here (a
   * declined publish is an expected outcome, not a bug).
   */
  async editorPublishCms(auth: CommitAuthorization): Promise<Receipt | Refusal> {
    const bridge = readEditorBridge();
    if (!bridge) return refuse(NO_EDITOR, true);

    // Pre-flight the pre-publish gate. The interactive dialog only appears when
    // there are metadata violations, and a tool call cannot answer it — so
    // instead of awaiting a dialog it would hang on, evaluate the same gate the
    // dialog is built from and refuse with the violations. The operator sees
    // exactly what the button's dialog would have shown, and can fix them or
    // publish from the toolbar where they can confirm.
    let detail;
    try {
      detail = await blogCreationService.get(bridge.contentId, auth.tenant);
    } catch {
      return refuse('Could not read the article to check it before publishing; nothing was published.', true);
    }
    const violations = buildGateItem(detail, auth.tenant).violations;
    if (violations.length > 0) {
      const list = violations.map((v) => v.message).join(' | ');
      return refuse(
        `The article has ${violations.length} pre-publish issue${violations.length === 1 ? '' : 's'} that must be resolved before it can go live: ${list}. ` +
          'Fix them, or publish from the toolbar where you can review and confirm. Nothing was published.',
        true,
      );
    }

    let html: string | undefined;
    try {
      html = await bridge.buildPublishHtml();
    } catch {
      return refuse('The article HTML could not be built, so nothing was published.', true);
    }
    if (!html) {
      return refuse('There is no article content to publish yet.', true);
    }

    // The article is clean, so `blogCreationService.publish` will not raise the
    // confirm dialog. A deadline backstop still guarantees the tool terminates
    // even if a late async check (a slug collision) surfaces a dialog nobody
    // answers: on timeout the tool refuses rather than hanging.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<typeof PUBLISH_TIMED_OUT>((resolve) => {
      timer = setTimeout(() => resolve(PUBLISH_TIMED_OUT), PUBLISH_DEADLINE_MS);
    });
    try {
      const outcome = await Promise.race([
        blogCreationService.publish(bridge.contentId, html, true, undefined, auth.tenant),
        deadline,
      ]);
      if (outcome === PUBLISH_TIMED_OUT) {
        return refuse(
          'A pre-publish confirmation is waiting that this tool cannot answer, so the publish did not complete. ' +
            'Publish from the toolbar to review and confirm it. Nothing was confirmed published.',
          true,
        );
      }
    } catch (err) {
      if (isPublishCancelledError(err)) {
        return refuse('The operator declined the pre-publish confirmation, so nothing was published.', true);
      }
      return refuse('WordPress did not confirm the publish, so the live state is unverified.', true);
    } finally {
      if (timer) clearTimeout(timer);
    }
    return {
      summary: "Published to the client's live WordPress site. This is now public.",
      facts: { contentId: bridge.contentId, reachedLiveSite: true },
    };
  },
};

/** Default export is what the mount merges by name into installCapabilities. */
export default editorSeams;
